import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Inbox } from "lucide-react";
import { http, auDateTime } from "./api";

const STATUS_TONE = {
  "Final output available": "border-ember/60 text-ember",
  "Clarification required": "border-ochre text-ochre",
  "Awaiting client information": "border-ochre text-ochre",
  "Engagement complete": "border-line text-stone2",
};

export default function PortalDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/portal/client/dashboard")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load your dashboard."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone2" data-testid="dashboard-loading">Loading dashboard…</p>;
  if (!data) return null;

  return (
    <div data-testid="portal-dashboard">
      <div className="mb-10">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-ochre mb-3">{data.organisation?.name}</p>
        <h1 className="font-serif text-3xl md:text-4xl">Yaama, {data.user.name || data.user.email}</h1>
      </div>

      <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-stone2 mb-4">Your engagements</h2>
      {data.engagements.length === 0 ? (
        <div className="border border-line bg-surface1 p-16 text-center mb-10">
          <Inbox className="mx-auto mb-4 text-stone2" size={32} />
          <p className="text-stone2">No engagements yet. Bigibila will set these up with you.</p>
        </div>
      ) : (
        <div className="space-y-px bg-line/40 border border-line/40 mb-10">
          {data.engagements.map((e, i) => (
            <Link key={e.id} to={`/portal/engagements/${e.id}`} data-testid={`engagement-card-${i}`} className="block bg-ink p-6 md:p-8 hover:bg-surface1 transition-colors duration-300">
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
                <h3 className="font-serif text-xl md:text-2xl">{e.title}</h3>
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1 border ${STATUS_TONE[e.status] || "border-line text-stone2"}`} data-testid={`engagement-status-${i}`}>{e.status}</span>
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-2 font-mono text-[11px] text-stone2">
                <span>Bigibila contact: <span className="text-bone">{e.bigibila_contact}</span></span>
                <span>Evidence submitted: <span className="text-bone">{e.evidence_submitted}</span></span>
                {e.evidence_needing_clarification > 0 && <span className="text-ochre">Clarification needed: {e.evidence_needing_clarification}</span>}
                <span>Approved outputs: <span className={e.approved_outputs > 0 ? "text-ember" : "text-bone"}>{e.approved_outputs}</span></span>
              </div>
              {e.client_actions?.length > 0 && (
                <div className="mt-4 border-l-2 border-ochre pl-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre mb-1.5">Upcoming client actions</p>
                  {e.client_actions.map((a, j) => <p key={j} className="text-sm text-stone2">— {a}</p>)}
                </div>
              )}
              {e.key_dates?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {e.key_dates.map((d, j) => (
                    <span key={j} className="font-mono text-[10px] px-2.5 py-1 border border-line text-stone2">{d.label || d}: {d.date || ""}</span>
                  ))}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-stone2 mb-4">Recent secure messages</h2>
      {data.recent_messages.length === 0 ? (
        <p className="text-sm text-stone2 border border-line bg-surface1 p-6">No messages yet.</p>
      ) : (
        <div className="space-y-px bg-line/40 border border-line/40">
          {data.recent_messages.map((m) => (
            <Link key={m.id} to={`/portal/engagements/${m.engagement_id}?tab=messages`} className="block bg-ink p-4 md:p-5 hover:bg-surface1 transition-colors duration-300">
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-ochre">{m.sender_role === "admin" ? "Bigibila" : m.sender_name}</span>
                <time className="font-mono text-[10px] text-stone2">{auDateTime(m.created_at)}</time>
              </div>
              <p className="text-sm text-stone2 line-clamp-2">{m.body}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
